import { useEffect, useMemo, useRef, useState } from "react";
import { Badge, Btn, Card, I } from "../ui";
import type {
  DiscardedFilter,
  NecrologioDTO,
  NecrologiStats,
  ProvinceDTO,
} from "../../api/contract";
import { getNecrologiStats } from "../api";
import { NecrologioDrawer } from "./necrologi/NecrologioDrawer";
import { EnqueuePreviewModal, SisterQueueCard } from "./necrologi/SisterQueuePanel";
import { NECROLOGI_PAGE_SIZE as PAGE_SIZE, useNecrologiSearch } from "./necrologi/useNecrologiSearch";
import { formatItalianDate, personName } from "../format";

export function Necrologi({ provinces, defaultScope, openSearchPicker = false }: {
  provinces: ProvinceDTO[];
  defaultScope?: { provincia: string; comune: string } | null;
  /** Arrivo dalla Panoramica: apre subito la scelta di chi cercare su SISTER. */
  openSearchPicker?: boolean;
}) {
  const [stats, setStats] = useState<NecrologiStats | null>(null);
  const [provincia, setProvincia] = useState(defaultScope?.provincia ?? "");
  const [comune, setComune] = useState(defaultScope?.comune ?? "");
  const [q, setQ] = useState("");
  const [source, setSource] = useState<string>("");
  const [vedovoOnly, setVedovoOnly] = useState(false);
  const [casaVuotaOnly, setCasaVuotaOnly] = useState(false);
  const [discarded, setDiscarded] = useState<DiscardedFilter>("exclude");
  const { data, setData, busy, err, offset, setOffset, reload } = useNecrologiSearch({
    provincia, comune, q, source, vedovoOnly, casaVuotaOnly, discarded,
  });
  const [selected, updateSelected] = useState<NecrologioDTO | null>(null);
  const selectionRef = useRef({ id: null as number | null, version: 0 });
  const selectionVersion = selectionRef.current.version;
  const mountedRef = useRef(true);
  const discardedRef = useRef(discarded);
  discardedRef.current = discarded;
  const [batchMsg, setBatchMsg] = useState<string | null>(null);
  // Anteprima accodamento: il bottone non accoda più alla cieca, apre la
  // selezione con checkbox (EnqueuePreviewModal).
  const [previewOpen, setPreviewOpen] = useState(openSearchPicker);
  const [showFilters, setShowFilters] = useState(false);
  const [previewScope, setPreviewScope] = useState<string | null>(openSearchPicker ? provincia || null : null);
  const [queueRefresh, setQueueRefresh] = useState(0);

  function setSelected(next: NecrologioDTO | null) {
    selectionRef.current = { id: next?.id ?? null, version: selectionRef.current.version + 1 };
    updateSelected(next);
  }

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  function openEnqueuePreview(scopeProvincia: string | null = provincia || null) {
    setPreviewScope(scopeProvincia);
    setPreviewOpen(true);
  }

  function refreshStatsAndList() {
    void getNecrologiStats().then(setStats).catch(() => setStats(null));
    reload();
  }

  useEffect(() => {
    void getNecrologiStats().then(setStats).catch(() => setStats(null));
  }, []);

  const provOptions = useMemo(() => {
    const codes = new Set(stats?.byProvincia.map((p) => p.provincia));
    const options: Pick<ProvinceDTO, "code" | "name">[] = provinces.filter((p) => !codes.size || codes.has(p.code));
    const names: Record<string, string> = { FG: "Foggia", VA: "Varese", NO: "Novara", BG: "Bergamo", PV: "Pavia", VE: "Venezia", VB: "Verbano-Cusio-Ossola", PN: "Pordenone", RO: "Rovigo" };
    for (const code of new Set([...codes, defaultScope?.provincia, provincia])) {
      if (code && !options.some(option => option.code === code)) options.push({ code, name: names[code] ?? code });
    }
    return options;
  }, [stats, provinces, defaultScope?.provincia, provincia]);

  const total = data?.total ?? 0;
  const pageEnd = Math.min(offset + PAGE_SIZE, total);
  const canPrev = offset > 0;
  const canNext = offset + PAGE_SIZE < total;

  return (
    <div className="col gap-16">
      <section className="step-card" aria-labelledby="necrologi-step-title">
        <div className="step-card-text">
          <h2 id="necrologi-step-title">Scegli chi cercare su SISTER</h2>
          <p>
            Qui arrivano i necrologi dalle fonti. Scegli le persone da cercare: l'estensione trova i loro immobili
            e li porta in Prospect.
          </p>
          <p className="step-card-meta">
            {stats
              ? `${stats.total.toLocaleString("it-IT")} necrologi · ${stats.last24h.toLocaleString("it-IT")} nelle ultime 24 ore`
              : "Caricamento…"}
            {(stats?.discardedCount ?? 0) > 0 && ` · ${stats!.discardedCount.toLocaleString("it-IT")} scartati`}
          </p>
          {batchMsg && <p className="step-card-msg" role="status">{batchMsg}</p>}
        </div>
        <div className="step-card-actions">
          <Btn
            variant="primary"
            onClick={() => openEnqueuePreview()}
            title="Apre l'elenco dei necrologi da cercare: scegli tu chi mandare all'estensione"
          >
            {provincia ? `Cerca su SISTER (${provincia})…` : "Cerca su SISTER…"}
          </Btn>
          <Btn variant="ghost" icon={I.refresh} onClick={() => {
            refreshStatsAndList();
            setOffset(0);
          }}>
            Aggiorna
          </Btn>
        </div>
      </section>

      {/* Coda SISTER: task attivi con annulla / porta in cima */}
      <SisterQueueCard
        refreshKey={queueRefresh}
        onMsg={(msg) => setBatchMsg(msg)}
      />

      <Card
        title="Cerca e filtra"
        actions={<Btn size="sm" variant="ghost" onClick={() => setShowFilters((v) => !v)}>{showFilters ? "Meno filtri" : "Altri filtri"}</Btn>}
      >
        <div className="row gap-8" style={{ flexWrap: "wrap", alignItems: "flex-end" }}>
          <div className="field" style={{ flex: 1, minWidth: 240 }}>
            <label className="label" htmlFor="necro-q">Cerca nel nome o nel testo</label>
            <input
              id="necro-q"
              className="input"
              placeholder="es. Ferrari, ved. Rossi, via Roma…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          <div className="field" style={{ minWidth: 180 }}>
            <label className="label" htmlFor="necro-prov">Provincia</label>
            <select id="necro-prov" className="select" value={provincia} onChange={(e) => setProvincia(e.target.value)}>
              <option value="">Tutte</option>
              {provOptions.map((p) => (
                <option key={p.code} value={p.code}>
                  {p.name} ({p.code})
                </option>
              ))}
            </select>
          </div>
          <div className="field" style={{ minWidth: 160 }}>
            <label className="label" htmlFor="necro-comune">Comune</label>
            <input
              id="necro-comune"
              className="input"
              placeholder="es. Piacenza"
              value={comune}
              onChange={(e) => setComune(e.target.value)}
            />
          </div>
          <label
            className="check-chip"
            title="Indicazione dal necrologio da verificare: non identifica gli eredi né conferma che la casa sia vuota."
          >
            <input type="checkbox" checked={casaVuotaOnly} onChange={(e) => setCasaVuotaOnly(e.target.checked)} />
            Solo coniuge non citato
          </label>
          <label className="check-chip" title="ved., vedovo o vedova nel nome o nel testo">
            <input type="checkbox" checked={vedovoOnly} onChange={(e) => setVedovoOnly(e.target.checked)} />
            Solo vedovi
          </label>
        </div>
        {showFilters && (
          <div className="row gap-8" style={{ flexWrap: "wrap", alignItems: "flex-end", marginTop: 12 }}>
            <div className="field" style={{ minWidth: 220 }}>
              <label className="label" htmlFor="necro-source">Fonte</label>
              <select id="necro-source" className="select" value={source} onChange={(e) => setSource(e.target.value)}>
                <option value="">Tutte</option>
                {(stats?.bySource ?? []).map((s) => (
                  <option key={s.source} value={s.source}>
                    {s.source} ({s.count.toLocaleString("it-IT")})
                  </option>
                ))}
              </select>
            </div>
            <div className="field" style={{ minWidth: 160 }}>
              <label className="label" htmlFor="necro-discarded">Necrologi scartati</label>
              <select id="necro-discarded" className="select" value={discarded} onChange={(e) => setDiscarded(e.target.value as DiscardedFilter)}>
                <option value="exclude">Nascondi</option>
                <option value="only">Solo scartati</option>
                <option value="include">Mostra anche</option>
              </select>
            </div>
          </div>
        )}
      </Card>

      {/* Tabella */}
      <Card
        title={`Risultati${total ? ` · ${total.toLocaleString("it-IT")}` : ""}`}
        sub={total ? `${offset + 1}-${pageEnd} di ${total.toLocaleString("it-IT")}` : "nessun risultato"}
        noPad
      >
        {err && (
          <div style={{ padding: 12, color: "var(--danger)", fontSize: 12 }}>{err}</div>
        )}
        {!err && !data && busy && (
          <div style={{ padding: 20, color: "var(--text-2)", fontSize: 13 }}>Caricamento…</div>
        )}
        {!err && data && data.items.length === 0 && (
          <div style={{ padding: 20, color: "var(--text-2)", fontSize: 13 }}>
            Nessun necrologio con questi filtri.
          </div>
        )}
        {!err && data && data.items.length > 0 && (
          <div style={{ overflowX: "auto" }}>
            <table className="tbl" style={{ width: "100%", fontSize: 12 }}>
              <thead>
                <tr>
                  <th style={{ textAlign: "left", padding: "8px 12px" }}>Decesso</th>
                  <th style={{ textAlign: "left", padding: "8px 12px" }}>Nome</th>
                  <th style={{ textAlign: "left", padding: "8px 12px" }}>Età</th>
                  <th style={{ textAlign: "left", padding: "8px 12px" }} title="Anno di nascita stimato: anno del decesso meno l'età (±1)">Anno nascita (circa)</th>
                  <th style={{ textAlign: "left", padding: "8px 12px" }}>Comune</th>
                  <th style={{ textAlign: "left", padding: "8px 12px" }}>Impresa</th>
                  <th style={{ textAlign: "left", padding: "8px 12px" }}>Fonte</th>
                  <th style={{ textAlign: "left", padding: "8px 12px" }}></th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((n) => (
                  <tr
                    key={n.id}
                    className="clickable-row"
                    tabIndex={0}
                    aria-label={`Apri il necrologio di ${personName(n.nome)}`}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setSelected(n);
                      }
                    }}
                    style={{
                      borderTop: "1px solid var(--line)",
                      cursor: "pointer",
                      background: selected?.id === n.id ? "var(--bg-2)" : undefined,
                      opacity: n.discardedAt != null ? 0.72 : undefined,
                    }}
                    onClick={() => setSelected(n)}
                  >
                    <td className="mono" style={{ padding: "8px 12px", whiteSpace: "nowrap" }}>
                      {formatItalianDate(n.dataDecesso)}
                    </td>
                    <td style={{ padding: "8px 12px", fontWeight: 500 }}>
                      <span className="row gap-6" style={{ alignItems: "center", flexWrap: "wrap" }}>
                        <span>{personName(n.nome)}</span>
                        {n.discardedAt != null && (
                          <Badge kind="muted">scartato</Badge>
                        )}
                        {n.vedovo && (
                          <Badge kind="info" title="Necrologio con ved./vedovo/vedova rilevato">ved.</Badge>
                        )}
                        {n.casaVuota && (
                          <Badge
                            kind="warn"
                            title="Coniuge non citato nel necrologio: indicazione da verificare. Non identifica gli eredi né conferma che la casa sia vuota."
                          >
                            Coniuge non citato · Da verificare
                          </Badge>
                        )}
                      </span>
                    </td>
                    <td className="mono" style={{ padding: "8px 12px" }}>{n.eta ?? "—"}</td>
                    <td className="mono" style={{ padding: "8px 12px", color: "var(--text-2)" }}>
                      {n.annoNascita ?? "—"}
                    </td>
                    <td style={{ padding: "8px 12px", color: "var(--text-2)" }}>
                      {n.comune || n.luogo || "—"}
                    </td>
                    <td style={{ padding: "8px 12px", color: "var(--text-2)" }}>
                      {n.impresaFunebre || "—"}
                    </td>
                    <td style={{ padding: "8px 12px" }}>
                      <Badge kind="muted">{n.source}</Badge>
                    </td>
                    <td style={{ padding: "8px 12px" }}>
                      {n.link && (
                        <a
                          href={n.link}
                          target="_blank"
                          rel="noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          style={{ color: "var(--accent)", fontSize: 11 }}
                          aria-label="Apri il necrologio sulla fonte"
                          title="Apri il necrologio sulla fonte"
                        >
                          ↗
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {data && (total > PAGE_SIZE || offset > 0) && (
          <div className="row gap-8" style={{ padding: 12, borderTop: "1px solid var(--line)", justifyContent: "flex-end" }}>
            <Btn size="sm" variant="ghost" disabled={!canPrev || busy} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>
              ← Precedente
            </Btn>
            <span className="mono muted" style={{ alignSelf: "center", fontSize: 11 }}>
              pagina {Math.floor(offset / PAGE_SIZE) + 1} / {Math.ceil(total / PAGE_SIZE)}
            </span>
            <Btn size="sm" variant="ghost" disabled={!canNext || busy} onClick={() => setOffset(offset + PAGE_SIZE)}>
              Successiva →
            </Btn>
          </div>
        )}
      </Card>

      <NecrologioDrawer
        key={selectionVersion}
        selected={selected}
        provincia={provincia}
        browserQueueBusy={false}
        onClose={() => setSelected(null)}
        onEnqueue={(scopeProvincia) => openEnqueuePreview(scopeProvincia)}
        onUpdated={(next) => {
          if (!mountedRef.current) return;
          const hidden = (discardedRef.current === "exclude" && next.discardedAt != null)
            || (discardedRef.current === "only" && next.discardedAt == null);
          const selection = selectionRef.current;
          if (selection.id === next.id && selection.version === selectionVersion) {
            if (hidden) setSelected(null);
            else updateSelected(next);
          }
          setData((prev) => {
            if (!prev) return prev;
            const items = hidden ? prev.items.filter((i) => i.id !== next.id) : prev.items.map((i) => i.id === next.id ? next : i);
            return { ...prev, items, total: hidden && items.length < prev.items.length ? Math.max(0, prev.total - 1) : prev.total };
          });
          refreshStatsAndList();
        }}
      />

      <EnqueuePreviewModal
        open={previewOpen}
        provincia={previewScope}
        onClose={() => setPreviewOpen(false)}
        onDone={(msg) => {
          setBatchMsg(msg);
          setQueueRefresh((v) => v + 1);
        }}
      />

    </div>
  );
}
